import React, { useState, useEffect } from "react";
import Container from "react-bootstrap/esm/Container";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Button,
  Form,
  InputGroup,
  Card,
  Alert,
  ProgressBar,
  Spinner
} from "react-bootstrap";
import { Eye, EyeSlash, CheckCircle, XCircle } from "react-bootstrap-icons";

import HeadingBanner from "../components/HeadingBanner";
import OrientationPrompt from "../components/OrientationPrompt";
import SimpleNavigationButton from "../components/SimpleNavigationButton";

import "../components/Recovery.css";
import { requestPasswordReset, updatePassword } from "../services/APIClient";

function Recovery() {
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isPasswordSent, setIsPasswordSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const paramEmail = searchParams.get("email");

  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasNumber: false
  });

  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (!email && !!paramEmail) {
      setEmail(paramEmail);
    }
  }, [paramEmail, email]);

  // Check password strength
  useEffect(() => {
    const hasMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    setPasswordFeedback({
      hasMinLength,
      hasUpperCase,
      hasNumber
    });

    // Calculate strength (0-100)
    let strength = 0;
    if (hasMinLength) strength += 40;
    if (hasUpperCase) strength += 30;
    if (hasNumber) strength += 30;

    setPasswordStrength(strength);
  }, [password]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const sendEmail = () => {
    setError(""); // Reset error
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Set email sent state immediately - no loading state needed
    setIsEmailSent(true);

    // Fire the API call in the background without waiting for response
    requestPasswordReset(email)
      .catch((error) => {
        console.error("Password reset error:", error);
        // We don't reset the isEmailSent state even on error
        // to avoid confusing the user who already saw the success message
      });
  };

  const sendPassword = () => {
    setError(""); // Reset error

    if (password !== passwordConfirm) {
      setError("Passwords do not match! Please retry.");
    } else if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
    } else if (!/[A-Z]/.test(password)) {
      setError("Password must contain at least one capital letter.");
    } else if (!/[0-9]/.test(password)) {
      setError("Password must contain at least one number.");
    } else {
      setIsLoading(true);
      updatePassword(password, token)
        .then((response) => {
          setIsPasswordSent(true);
          setError("");
          setIsLoading(false);
        })
        .catch((error) => {
          setError("Failed to update password. Please try again.");
          setIsLoading(false);
        });
    }
  };

  // Password Success Screen
  if (isPasswordSent) {
    return (
      <Container fluid>
        <HeadingBanner
          isLoggedIn={false}
          cloud={true}
          name={"Assessing Letter Knowledge & Phonemic Awareness Class Assistant"}
        />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
          <Card className="recovery-card text-center shadow">
            <Card.Body className="p-4">
              <div className="success-icon mb-4">
                <CheckCircle size={60} className="text-success" />
              </div>
              <h2 className="mb-4">Password Reset Successfully!</h2>
              <p className="mb-4">Your new password has been saved. You can now log in with your new credentials.</p>
              <Link to={"/"}>
                <Button variant="primary" size="lg" className="w-100">
                  Return to Login Screen
                </Button>
              </Link>
            </Card.Body>
          </Card>
        </div>
        {isPortrait && <OrientationPrompt />}
      </Container>
    );
  }

  // Initial Email Request Screen
  if (!token) {
    return (
      <Container fluid>
        <HeadingBanner
          isLoggedIn={false}
          cloud={true}
          name={"Assessing Letter Knowledge & Phonemic Awareness Class Assistant"}
        />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
          <Card className="recovery-card shadow">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <img
                  src="/assets/imgs/forgotpw.png"
                  alt="Forgot Password"
                  className="recovery-image"
                  style={{ width: "100px", height: "auto" }}
                />
                <h2 className="mt-3">Reset Your Password</h2>
                <p className="text-muted">Enter your email to receive a password reset link</p>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {isEmailSent && !error && (
                <div className="message alert alert-success mt-2">
                  <p>Email Sent! Please check your inbox.</p>
                  <p>
                    If you don't see our email within the next 5 minutes, kindly reach
                    out to us at info@alpaca-assessment.com for further assistance.
                  </p>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isEmailSent || isLoading}
                  className="recovery-email-input"
                />
              </Form.Group>

              <div className="d-grid gap-2">
                {!isEmailSent && (
                  <Button
                    variant="primary"
                    onClick={sendEmail}
                    disabled={isEmailSent}
                  >
                    Send Reset Link
                  </Button>
                )}
                <Link to="/" className="text-decoration-none">
                  <Button variant="outline-secondary" className="w-100">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </Card.Body>
          </Card>
        </div>
        {isPortrait && <OrientationPrompt />}
      </Container>
    );
  }

  // Password Reset Screen
  return (
    <Container fluid>
      <HeadingBanner
        isLoggedIn={false}
        cloud={true}
        name={"Assessing Letter Knowledge & Phonemic Awareness Class Assistant"}
      />
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
        <Card className="recovery-card shadow">
          <Card.Body className="p-4">
            <h2 className="text-center mb-3">Create New Password</h2>
            <p className="text-center text-muted mb-4">
              For account: <strong>{email}</strong>
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-4">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  aria-describedby="password-toggle"
                />
                <Button
                  variant="outline-secondary"
                  id="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </InputGroup>

              <div className="mt-2">
                <ProgressBar
                  now={passwordStrength}
                  variant={
                    passwordStrength < 40 ? "danger" :
                      passwordStrength < 70 ? "warning" :
                        "success"
                  }
                  className="mb-2"
                />
                <div className="password-requirements">
                  <div className={passwordFeedback.hasMinLength ? "text-success" : "text-danger"}>
                    {passwordFeedback.hasMinLength ? <CheckCircle className="me-1" /> : <XCircle className="me-1" />}
                    At least 8 characters
                  </div>
                  <div className={passwordFeedback.hasUpperCase ? "text-success" : "text-danger"}>
                    {passwordFeedback.hasUpperCase ? <CheckCircle className="me-1" /> : <XCircle className="me-1" />}
                    At least 1 capital letter
                  </div>
                  <div className={passwordFeedback.hasNumber ? "text-success" : "text-danger"}>
                    {passwordFeedback.hasNumber ? <CheckCircle className="me-1" /> : <XCircle className="me-1" />}
                    At least 1 number
                  </div>
                </div>
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Confirm your password"
                  aria-describedby="confirm-password-toggle"
                />
                <Button
                  variant="outline-secondary"
                  id="confirm-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </InputGroup>
              {password && passwordConfirm && (
                <div className="mt-2">
                  {password === passwordConfirm ? (
                    <span className="text-success">
                      <CheckCircle className="me-1" /> Passwords match
                    </span>
                  ) : (
                    <span className="text-danger">
                      <XCircle className="me-1" /> Passwords don't match
                    </span>
                  )}
                </div>
              )}
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                onClick={sendPassword}
                disabled={isLoading || !(passwordFeedback.hasMinLength && passwordFeedback.hasUpperCase && passwordFeedback.hasNumber && password === passwordConfirm)}
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Updating...</span>
                  </>
                ) : (
                  "Save New Password"
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default Recovery;